import React, {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
    TextField,
    Grid,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    InputAdornment, IconButton
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import {getAgentPrediction, AgentPredictionResponse} from '../../services/hipodromo';
import {useAuth} from '../../context/Login.context';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {getColors} from '../../componetes/color_racers'; // Importar getColors

const components = {
    table: ({node, ...props}: any) => <TableContainer component={Paper} sx={{maxHeight: 440, overflowX: 'auto'}}><Table
        stickyHeader {...props} /></TableContainer>,
    thead: ({node, ...props}: any) => <TableHead {...props} />,
    tbody: ({node, ...props}: any) => <TableBody {...props} />,
    tr: ({node, ...props}: any) => <TableRow {...props} />,
    td: ({node, ...props}: any) => <TableCell sx={{padding: '8px', whiteSpace: 'nowrap'}} {...props} />,
    th: ({node, ...props}: any) => <TableCell
        sx={{padding: '8px', fontWeight: 'bold', whiteSpace: 'nowrap'}} {...props} />,
    h1: ({node, ...props}: any) => <Typography variant="h4" gutterBottom sx={{mb: 1}} {...props} />,
    h2: ({node, ...props}: any) => <Typography variant="h5" gutterBottom sx={{mb: 1}} {...props} />,
    h3: ({node, ...props}: any) => <Typography variant="h6" gutterBottom sx={{mb: 1}} {...props} />,
    h4: ({node, ...props}: any) => <Typography variant="subtitle1" gutterBottom
                                               sx={{mb: 1, fontWeight: 'bold'}} {...props} />,
    h5: ({node, ...props}: any) => <Typography variant="subtitle2" gutterBottom
                                               sx={{mb: 1, fontWeight: 'bold'}} {...props} />,
    h6: ({node, ...props}: any) => <Typography variant="body1" gutterBottom
                                               sx={{mb: 1, fontWeight: 'bold'}} {...props} />,
    p: ({node, ...props}: any) => <Typography variant="body2" paragraph sx={{mb: 1}} {...props} />,
    ul: ({node, ...props}: any) => <ul style={{marginTop: 0, marginBottom: '8px', paddingLeft: '20px'}} {...props} />,
    ol: ({node, ...props}: any) => <ol style={{marginTop: 0, marginBottom: '8px', paddingLeft: '20px'}} {...props} />,
    li: ({node, ...props}: any) => <li style={{marginBottom: '4px'}}><Typography variant="body2"
                                                                                 component="span" {...props} /></li>,
};

const AgentView: React.FC = () => {
    const {hipodromoId, id} = useParams<{ hipodromoId: string, id: string }>();
    const [agentResponse, setAgentResponse] = useState<AgentPredictionResponse | null>(null); // Almacenar la respuesta completa
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [prompt, setPrompt] = useState<string>('');
    const {user} = useAuth();

    const fetchPrediction = async (currentPrompt: string) => {
        if (!user || (!user.subscription?.name.includes('Premium IA') && !user.subscription?.name.includes('Freemium IA'))) {
            setError('Necesitas una suscripción Premium para acceder a esta función.');
            setLoading(false);
            return;
        }

        if (!hipodromoId) {
            setError('ID de hipódromo no proporcionado.');
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);
            const hId = hipodromoId ? Number(hipodromoId) : 1;
            const data = await getAgentPrediction(Number(id), currentPrompt, hId);
            setAgentResponse(data);
        } catch (err: any) {
            setError(err.message || 'Error al obtener la predicción del agente.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!user || (!user.subscription?.name.includes('Premium IA') && !user.subscription?.name.includes('Freemium IA'))) {
            setError('Necesitas una suscripción Premium para acceder a esta función.');
        } else if (!hipodromoId) {
            setError('ID de hipódromo no proporcionado.');
        }
    }, [hipodromoId, user]);

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        fetchPrediction(prompt);
    };

    if (error) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh" p={2}>
                <Alert severity="error" sx={{width: '100%', maxWidth: '600px'}}>{error}</Alert>
            </Box>
        );
    }

    // Ordenar proximas_carreras_data por inscripcion
    const sortedCarrerasData = agentResponse?.proximas_carreras_data
        ? [...agentResponse.proximas_carreras_data].sort((a, b) => a.inscripcion - b.inscripcion)
        : [];

    return (
        <Box sx={{p: 3, textAlign: 'center'}}>
            <Typography variant="h4" gutterBottom>
                Consulta a tu Agente Hípico
            </Typography>
            <Box component="form" onSubmit={handleSubmit} sx={{mt: 3, mb: 3}}>
                <TextField
                    label="Ingresa tu consulta para el análisis"
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={2}
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    sx={{mb: 2}}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    onClick={handleSubmit}
                                    edge="end"
                                    disabled={loading}
                                >
                                    {loading ? <CircularProgress size={20}/> : <SendIcon/>}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>
            {agentResponse ? (
                <Grid container spacing={3} sx={{mt: 4}}>
                    {/* Columna para el resumen del análisis */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{height: '100%', textAlign: 'left'}}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Resumen del Análisis:
                                </Typography>
                                <Box
                                    sx={{textAlign: 'left'}}
                                >
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}
                                                   components={components}>
                                        {agentResponse.analysis_summary.replace(/\n{3,}/g, '\n\n')}
                                    </ReactMarkdown>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Columna para los datos de las próximas carreras */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{height: '100%', textAlign: 'left'}}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Datos de Próximas Carreras:
                                </Typography>
                                {sortedCarrerasData.length > 0 ? (
                                    sortedCarrerasData.map((carrera: any) => {
                                        const {color, textColor} = getColors(carrera.inscripcion);
                                        console.log(textColor)
                                        return (
                                            <Box key={carrera.eje_id} sx={{
                                                mb: 2,
                                                p: 1,
                                                borderLeft: `5px solid ${color}`,
                                                backgroundColor: `${color}10`
                                            }}>
                                                <Typography variant="subtitle1" sx={{fontWeight: 'bold'}}>
                                                    #{carrera.inscripcion} - {carrera.eje_name}
                                                </Typography>
                                                <Typography variant="body2">Predicción
                                                    Oficial: {(carrera.prediccion_oficial * 100).toFixed(2)}%</Typography>
                                                <Typography variant="body2">Ranking
                                                    Score: {carrera.ranking_score}</Typography>
                                                <Typography variant="body2">Handicap: {carrera.handicap_promedio}</Typography>
                                                {/* Puedes añadir más detalles de la carrera aquí si los necesitas */}
                                            </Box>
                                        );
                                    })
                                ) : (
                                    <Typography variant="body2">No hay datos de próximas carreras
                                        disponibles.</Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            ) : (
                <Typography variant="body1">
                    Ingresa un prompt y haz clic en 'Obtener Análisis' para ver la predicción.
                </Typography>
            )}
        </Box>
    );
};

export default AgentView;

